import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { categoriesApi, examsApi } from '../../services/api';

const inputClass = 'w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

interface CategoryForm {
  name: string;
  nameEn: string;
  slug: string;
  description: string;
  descriptionEn: string;
  icon: string;
  examId: string;
  order: number;
  isActive: boolean;
}

const defaultForm: CategoryForm = {
  name: '',
  nameEn: '',
  slug: '',
  description: '',
  descriptionEn: '',
  icon: '📖',
  examId: '',
  order: 0,
  isActive: true,
};

export function CategoryEditor() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEditing = !!id;

  const [form, setForm] = useState<CategoryForm>({
    ...defaultForm,
    examId: searchParams.get('exam') ?? '',
  });
  const [exams, setExams] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    examsApi.getAll().then(({ data }) => {
      setExams(Array.isArray(data) ? data : data.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    categoriesApi.getById(id)
      .then(({ data }) => {
        setForm({
          name: data.name ?? '',
          nameEn: data.nameEn ?? '',
          slug: data.slug ?? '',
          description: data.description ?? '',
          descriptionEn: data.descriptionEn ?? '',
          icon: data.icon ?? '📖',
          examId: data.examId ?? '',
          order: data.order ?? 0,
          isActive: data.isActive ?? true,
        });
      })
      .catch(() => toast.error('Impossible de charger la catégorie'))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const generateSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('Le nom et le slug sont requis');
      return;
    }
    if (!form.examId) {
      toast.error('Veuillez sélectionner un examen');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await categoriesApi.update(id, form);
        toast.success('Catégorie mise à jour');
      } else {
        await categoriesApi.create(form);
        toast.success('Catégorie créée');
      }
      navigate('/admin/categories');
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-xl space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/categories')} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isEditing ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 space-y-5">
        <Field label="Examen *">
          <select
            className={inputClass}
            value={form.examId}
            onChange={(e) => setForm({ ...form, examId: e.target.value })}
          >
            <option value="">— Sélectionner un examen —</option>
            {exams.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Nom (français) *">
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="ex: Algèbre"
            />
          </Field>
          <Field label="Nom (anglais)">
            <input
              type="text"
              className={inputClass}
              value={form.nameEn}
              onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
              placeholder="e.g. Algebra"
            />
          </Field>
        </div>

        <Field label="Slug *">
          <input
            type="text"
            className={inputClass}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            placeholder="ex: algebre"
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Description (français)">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Description de la catégorie..."
            />
          </Field>
          <Field label="Description (anglais)">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.descriptionEn}
              onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })}
              placeholder="Category description..."
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-5">
          <Field label="Icône (emoji)">
            <input
              type="text"
              className={inputClass}
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              placeholder="📖"
            />
          </Field>
          <Field label="Ordre d'affichage">
            <input
              type="number"
              min={0}
              className={inputClass}
              value={form.order}
              onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Active (visible dans l'application)</span>
        </label>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm disabled:opacity-50"
        >
          <Save size={16} />
          {saving ? 'Sauvegarde...' : isEditing ? 'Mettre à jour' : 'Créer la catégorie'}
        </button>
      </div>
    </div>
  );
}
